import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add, Delete, UploadFile, ViewColumn } from "@mui/icons-material";
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore as db } from "../../firebase";

const ChecklistForm = ({ email, onClose, selectedChecklist, onSave }) => {
  // --- State ---
  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [technicianList, setTechnicianList] = useState([]);
  const [assignedEmail, setAssignedEmail] = useState("");

  const isAssignedChecklist = !!selectedChecklist?.assignedTo;
  const storage = getStorage();

  // --- UPDATED: Fetch Technicians with Debugging & Tenant Logic ---
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const tenantId = sessionStorage.getItem("tenantId");
        const userRole = sessionStorage.getItem("userRole");

        console.log("--- DEBUG CHECKLIST ASSIGN ---");
        console.log("Tenant ID:", tenantId);
        console.log("User Role:", userRole);

        // Base Constraints - fetch all users from tenant
        const constraints = [];
        if (tenantId && userRole !== "superadmin") {
          constraints.push(where("tenantId", "==", tenantId));
        }

        const q = query(collection(db, "UserProfileMaster"), ...constraints);
        const snapshot = await getDocs(q);

        // Filter for technician role (handle both "technician" and "Technician")
        const list = snapshot.docs
          .filter((doc) => {
            const role = doc.data().role;
            return role === "technician" || role === "Technician";
          })
          .map((doc) => {
            const data = doc.data();
            return {
              ...data,
              displayName: data.displayName || data.name || data.firstName || data.email || "Unknown",
              email: data.email,
            };
          });

        console.log(`Found ${list.length} technicians`); // Debug Log
        setTechnicianList(list);
      } catch (error) {
        console.error("Error fetching technicians:", error);
      }
    };

    fetchTechnicians();
  }, []);

  // --- Populate Form on Edit ---
  useEffect(() => {
    if (selectedChecklist) {
      setChecklistName(selectedChecklist.name || "");
      setDescription(selectedChecklist.description || "");
      setRemarks(selectedChecklist.remarks || "");
      setImageUrl(selectedChecklist.imageUrl || "");
      setColumns(selectedChecklist.columns || []);
      setRows(selectedChecklist.rows || []);
      setAssignedEmail(selectedChecklist.assignedTo || "");
    } else {
      // Default structure for new checklist
      const defaultCols = [
        { id: uuidv4(), name: "Item ID" },
        { id: uuidv4(), name: "Product Description" },
        { id: uuidv4(), name: "Quantity" },
        { id: uuidv4(), name: "Checkbox" }, // Checkbox is last by default
      ];
      setColumns(defaultCols);
      setRows([{ id: uuidv4(), data: {} }]);
    }
  }, [selectedChecklist]);

  // --- Logic for Table Management ---
  const handleAddRow = () => {
    const newRow = { id: uuidv4(), data: {} };
    setRows([...rows, newRow]);
  };

  const handleAddColumn = () => {
    const newCol = { id: uuidv4(), name: "New Column" };
    // Find the index of the Checkbox column
    const checkboxIndex = columns.findIndex((col) => col.name === "Checkbox");

    if (checkboxIndex !== -1) {
      // Insert before Checkbox
      const updatedColumns = [...columns];
      updatedColumns.splice(checkboxIndex, 0, newCol);
      setColumns(updatedColumns);
    } else {
      setColumns([...columns, newCol]);
    }
  };

  const handleColumnNameChange = (id, newName) => {
    const updatedCols = columns.map((col) =>
      col.id === id ? { ...col, name: newName } : col
    );
    setColumns(updatedCols);
  };

  const handleRemoveRow = (id) => {
    const updatedRows = rows.filter((row) => row.id !== id);
    setRows(updatedRows);
  };

  const handleRowChange = (rowId, colId, value) => {
    const updatedRows = rows.map((row) => {
      if (row.id === rowId) {
        return {
          ...row,
          data: { ...row.data, [colId]: value },
        };
      }
      return row;
    });
    setRows(updatedRows);
  };

  // --- Save Logic ---
  const handleSave = async () => {
    try {
      const tenantId = sessionStorage.getItem("tenantId");
      const userRole = sessionStorage.getItem("userRole");
      const checklistData = {
        name: checklistName,
        description,
        remarks,
        imageUrl,
        assignedTo: assignedEmail,
        columns,
        rows,
        createdBy: email,
        createdAt: new Date(),
        IsActive: true,
        tenantId: userRole === "superadmin" ? "" : tenantId || "",
      };
      if (selectedChecklist) {
        const docRef = isAssignedChecklist
          ? doc(db, "assignedChecklists", selectedChecklist.id)
          : doc(db, "checklists", selectedChecklist.id);
        await updateDoc(docRef, checklistData);
      } else {
        await addDoc(collection(db, "checklists"), checklistData);
      }
      if (onSave) onSave(checklistData);
      onClose();
    } catch (error) {
      console.error("Error saving checklist:", error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uniqueFileName = `${Date.now()}-${file.name}`;
      const imageRef = ref(storage, `checklist_images/${uniqueFileName}`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);
      setImageUrl(url);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {selectedChecklist ? "Edit Checklist" : "Create Checklist"}
      </DialogTitle>

      <DialogContent dividers>
        {/* Top Section: Metadata */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Checklist Name"
              fullWidth
              value={checklistName}
              onChange={(e) => setChecklistName(e.target.value)}
            />
            {!isAssignedChecklist && (
              <FormControl fullWidth>
                <InputLabel id="assign-label">Assign To</InputLabel>
                {/* --- UPDATED SELECT COMPONENT --- */}
                <Select
                  labelId="assign-label"
                  value={assignedEmail}
                  onChange={(e) => setAssignedEmail(e.target.value)}
                  label="Assign To"
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>-- Select Technician --</em>
                  </MenuItem>
                  {technicianList.length > 0 ? (
                    technicianList.map((tech, index) => (
                      <MenuItem key={tech.email || index} value={tech.email}>
                        {tech.displayName} ({tech.email || "No Email"})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      <em>No Technicians Found</em>
                    </MenuItem>
                  )}
                </Select>
                {/* --- END UPDATED SELECT COMPONENT --- */}
              </FormControl>
            )}
          </Box>

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Image Upload Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFile />}
            >
              Upload Image
              <input type="file" hidden onChange={handleImageUpload} />
            </Button>
            {imageUrl && (
              <Box
                component="img"
                src={imageUrl}
                alt="Uploaded"
                sx={{ height: 50, borderRadius: 1, border: "1px solid #ddd" }}
              />
            )}
          </Box>
        </Box>

        {/* Table Structure */}
        <Typography variant="h6" gutterBottom>
          Checklist Items
        </Typography>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={{ minWidth: 150 }}>
                      <TextField
                        variant="standard"
                        fullWidth
                        value={col.name}
                        onChange={(e) =>
                          handleColumnNameChange(col.id, e.target.value)
                        }
                        disabled={col.name === "Checkbox"}
                        InputProps={{
                          disableUnderline: true,
                          style: { fontWeight: "bold", fontSize: "0.875rem" },
                        }}
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.name === "Checkbox" ? (
                          <Checkbox
                            checked={!!row.data[col.id]}
                            onChange={(e) =>
                              handleRowChange(row.id, col.id, e.target.checked)
                            }
                            color="primary"
                          />
                        ) : (
                          <TextField
                            variant="standard"
                            fullWidth
                            value={row.data[col.id] || ""}
                            onChange={(e) =>
                              handleRowChange(row.id, col.id, e.target.value)
                            }
                            placeholder={`Enter ${col.name}`}
                            InputProps={{ disableUnderline: true }}
                            sx={{
                              "& .MuiInputBase-root": { padding: "4px" },
                            }}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveRow(row.id)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button startIcon={<Add />} onClick={handleAddRow} variant="outlined">
            Add Row
          </Button>
          <Button
            startIcon={<ViewColumn />}
            onClick={handleAddColumn}
            variant="outlined"
            color="secondary"
          >
            Add Column
          </Button>
        </Box>

        {/* Bottom Section: Remarks */}
        <TextField
          label="Remarks"
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 3 }}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Checklist
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistForm;
